import {Alert} from 'antd';
import {useAccessPolicy} from '../useAccessPolicy';
import { AdminUserMenu } from "./AdminUserMenu";
import ProjectSwitcher from './ProjectSwitcher';
import type { ReactNode } from "react";
import { Activity, AudioLines, BarChart3, ChevronRight, ClipboardCheck, Database, Headphones, GitCompareArrows, Home, LayoutDashboard, PlayCircle, SlidersHorizontal } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import "../styles/shell.css";

const navigation = [
  { label: "工作台", to: "/", icon: LayoutDashboard },
  { label: "数据集版本", to: "/datasets", icon: Database },
  { label: "评测任务", to: "/runs", icon: PlayCircle },
  { label: "评测方案", to: "/metrics", icon: SlidersHorizontal },
  { label: "Reference 验收", to: "/reference-acceptance", icon: ClipboardCheck },
  { label: "模型盲听", to: "/blind-studies", icon: Headphones },
  { label: "版本对比", to: "/comparisons", icon: GitCompareArrows },
  { label: "结果分析", to: "/analysis", icon: BarChart3 },
  { label: "疑似听审", to: "/reviews", icon: Headphones },
  { label: "Bad Case 回归", to: "/bad-cases", icon: ClipboardCheck },
] as const;

const pageNames: Record<string, string> = {
  "/settings/observability": "监控与日志",
  "/admin-account": "账号管理", "/": "工作台", "/datasets": "数据集版本", "/runs": "评测任务",
  "/runs/new/reference-boundary": "创建 V63 边界评测", "/metrics": "评测方案",
  "/blind-studies": "模型盲听", "/reference-acceptance": "Reference 验收",
  "/reference-library": "Reference 音频库",
  "/comparisons": "版本对比", "/analysis": "结果分析", "/reviews": "疑似听审",
  "/bad-cases": "Bad Case 回归", "/settings": "平台状态",
};

export interface AppShellProps { children?: ReactNode }

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const access = useAccessPolicy();
  const pageName = pageNames[location.pathname] ?? navigation.find(
    (item) => item.to !== "/" && location.pathname.startsWith(item.to),
  )?.label ?? "工作台";

  return <div className="bench-shell">
    <aside className="bench-sidebar">
      <div className="bench-brand">
        <span className="bench-brand__mark" aria-hidden="true"><AudioLines size={24} strokeWidth={2.2} /></span>
        <span className="bench-brand__text"><b>VUILabs</b><small>语音评测平台</small></span>
      </div>
      <nav className="bench-navigation" aria-label="主导航">
        {navigation.map(({ label, to, icon: Icon }) => <NavLink key={to} to={to} end={to === "/"}
          className={({ isActive }) => `bench-navigation__item${isActive ? " is-active" : ""}`} title={label}>
          <Icon size={19} strokeWidth={1.8} aria-hidden="true" /><span>{label}</span>
        </NavLink>)}
      </nav>
      <nav className="bench-utility-navigation" aria-label="平台信息">
        <NavLink to="/settings" title="平台状态" className={({ isActive }) => `bench-navigation__item${isActive ? " is-active" : ""}`}>
          <Activity size={18} aria-hidden="true" /><span>平台状态</span>
        </NavLink>
      </nav>
    </aside>
    <div className="bench-shell__main">
      <header className="bench-topbar">
        <div className="bench-breadcrumb" aria-label="面包屑"><Home size={16} aria-hidden="true" /><ChevronRight size={14} aria-hidden="true" /><span>{pageName}</span></div>
        <div style={{display:'flex',gap:16,alignItems:'center'}}><ProjectSwitcher/><AdminUserMenu /></div>
      </header>
      <main className="bench-content">{access.noProject&&<Alert showIcon type="info" message="尚未分配项目" description="请联系管理员将你的账号加入项目。获得授权前无法查看业务数据或新建评测；历史私有数据不会自动开放。" style={{marginBottom:16}}/>}{access.error&&<Alert showIcon type="warning" message="权限信息暂不可用，操作入口已暂时关闭" style={{marginBottom:16}}/>}{children ?? <Outlet />}</main>
    </div>
  </div>;
}

export default AppShell;
